import { Link } from "react-router-dom";
import { Heart, ArrowRight } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1493130952181-47e36589f64d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxkYXduJTIwbGlnaHQlMjBicmVha2luZyUyMGNsb3Vkc3xlbnwwfHx8fDE3ODMwNDQ2MTV8MA&ixlib=rb-4.1.0&q=85";

const MOVEMENTS = [
  {
    eyebrow: "On seeking",
    title: "Not a religion — a returning",
    body: [
      "We believe Christ did not come to hand us one more religion to perform. So much of what we were given teaches the soul to search — hour after hour — for a God kept far off, just out of reach.",
      "But the search was always meant to end within. And God, in His gentleness, gave us free will — not a set of chains, but the quiet dignity of choosing love for ourselves.",
    ],
    verse: "“Neither shall they say, Lo here! or, lo there! for, behold, the kingdom of God is within you.”",
    ref: "Luke 17:21",
  },
  {
    eyebrow: "One family",
    title: "We are all brothers and sisters",
    body: [
      "Whatever name you were taught for the One who made you, whatever form you hold Him in — we were shaped by the same hand. That makes us family.",
      "Here there is no stranger. Only a brother or a sister you have not yet sat beside. We come closer not by agreeing on everything, but by working — together — on ourselves, for one another, for every relationship we are held in.",
    ],
    verse: "“A new commandment I give unto you, That ye love one another.”",
    ref: "John 13:34",
  },
  {
    eyebrow: "Grace",
    title: "There is no judgment here",
    body: [
      "This is not a place of judgment. We are told that one day we will each be seen fully by Him — and that reckoning is His to hold, never ours.",
      "So we lay the stone down. We would far rather tend the plank in our own eye than the speck in a brother's. What you bring here is safe. You are met, not measured.",
    ],
    verse: "“He that is without sin among you, let him first cast a stone.”",
    ref: "John 8:7",
  },
  {
    eyebrow: "The work",
    title: "Bringing heaven to earth",
    body: [
      "The kingdom of heaven is not a far country we hope to reach one day. It is already within you, waiting to be lived.",
      "When we do the quiet inner work — the honesty, the forgiveness, the softening — we begin to carry that heaven into the ground beneath our feet. And if enough of us do it, gently and together, we bring it to earth.",
    ],
    verse: "“Thy kingdom come. Thy will be done in earth, as it is in heaven.”",
    ref: "Matthew 6:10",
  },
  {
    eyebrow: "The gift",
    title: "What He came to show us",
    body: [
      "This is what we believe Christ came to reveal, and to do for us: that the veil is torn, the distance is over, and the very Light we chased outside was already home inside us.",
      "We work on ourselves not to earn our way in — but because we are already loved. And love, once it is remembered, cannot help but overflow toward everyone we meet.",
    ],
    verse: "“That they all may be one... I in them, and thou in me.”",
    ref: "John 17:21-23",
  },
];

export default function OurHeart() {
  return (
    <main data-testid="our-heart-page">
      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden rounded-b-[2.5rem]">
        <img src={HERO} alt="Dawn light breaking through clouds" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#17100C] via-[#17100C]/85 to-[#17100C]/40" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 pb-20 w-full fade-up">
          <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-5 flex items-center gap-2">
            <Heart size={14} /> Our Heart
          </p>
          <h1 className="serif text-4xl sm:text-6xl lg:text-7xl font-light text-[#F7F1E6] leading-[1.05]">
            The kingdom of heaven <span className="italic-serif text-[#D4AF37]">is within you.</span>
          </h1>
          <p className="mt-8 text-lg text-[#C9BCA8] leading-relaxed max-w-2xl">
            Not a new religion. A returning — home to the Light we were made from, and to one another
            as brothers and sisters, so that together we might bring a little heaven to this earth.
          </p>
        </div>
      </section>

      {/* MOVEMENTS */}
      <section className="max-w-3xl mx-auto px-6 py-20 space-y-20">
        {MOVEMENTS.map((m, i) => (
          <div key={i} data-testid={`heart-movement-${i}`} className="fade-up" style={{ animationDelay: `${i * 0.05}s` }}>
            <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-3">
              {String(i + 1).padStart(2, "0")} · {m.eyebrow}
            </p>
            <h2 className="serif text-3xl sm:text-4xl text-[#F7F1E6] font-light leading-tight">{m.title}</h2>
            <div className="mt-6 space-y-4">
              {m.body.map((p, j) => (
                <p key={j} className="text-[#B4A897] leading-relaxed text-lg">{p}</p>
              ))}
            </div>
            <blockquote className="mt-8 border-l-2 pl-6 italic-serif text-2xl sm:text-3xl text-[#C1272D] leading-snug" style={{ borderColor: "rgba(193,39,45,0.5)" }}>
              {m.verse}
              <footer className="mt-3 not-italic text-sm text-[#D4AF37] tracking-wide font-sans">— {m.ref} (KJV)</footer>
            </blockquote>
          </div>
        ))}
      </section>

      {/* CLOSING */}
      <section className="max-w-4xl mx-auto px-6 pb-24">
        <div className="glass rounded-3xl p-10 sm:p-16 text-center" data-testid="heart-closing">
          <p className="italic-serif text-2xl sm:text-4xl text-[#F7F1E6] leading-snug max-w-3xl mx-auto">
            There is no judgment here — only the invitation to sit with your own truth, to soften,
            and to become, quietly, a place where heaven touches the earth.
          </p>
          <p className="mt-6 text-[#D4AF37] tracking-wide">— Mirrors of Your Soul</p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link
              to="/journal"
              data-testid="heart-to-journal"
              className="group inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#17100C] px-8 py-3.5 font-medium hover:bg-[#e6c860] transition-colors duration-300 gold-glow"
            >
              Begin the inner work
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <Link
              to="/common-thread"
              data-testid="heart-to-common-thread"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-3.5 text-[#F7F1E6] hover:border-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors duration-300"
            >
              See the common thread
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
